import { differenceInYears } from "date-fns";
import { parseFullName } from "parse-full-name";

import type { Patient } from "@elation/api-client/src/stitch-web/models";
import type { PatientSearchItem } from "@elation/api-client/src/stitch-web/models/patientSearchItem";
import type { SearchPatientsSortBy } from "@elation/api-client/src/stitch-web/models/searchPatientsSortBy";

import { PRODUCT_NAME } from "@src/shared/brand";
import { formatPostalAddress } from "@src/shared/utils/address";
import { parseNullableDate } from "@src/shared/utils/dates";
import { compactStrings } from "@src/shared/utils/strings";

type PatientStatus = Patient["status"];
type MaybePatient = Patient | null | undefined;
type PatientIdentityLike = {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  actualName?: string | null;
};
type MaybePatientDisplayIdentity =
  | Patient
  | PatientSearchItem
  | PatientIdentityLike
  | null
  | undefined;
type MaybePatientIdentity = Patient | PatientSearchItem | null | undefined;
type MaybePatientSearchItem = PatientSearchItem | null | undefined;

export type PatientSyncState = NonNullable<PatientSearchItem["syncStatus"]>;

// Mirrors the server's `NGRAM_MIN_TOKEN_SIZE` after Boolean-mode sanitization.
const MIN_RELEVANCE_QUERY_LENGTH = 2;
const BOOLEAN_OPERATOR_RE = /[+\-"()<>~*@]/g;

function hasUsableRelevanceQuery(query: string): boolean {
  const sanitizedQuery = query
    .replace(BOOLEAN_OPERATOR_RE, " ")
    .replace(/\s+/g, " ")
    .trim();

  return sanitizedQuery
    .split(" ")
    .some((token) => token.length >= MIN_RELEVANCE_QUERY_LENGTH);
}

const EHR_LABELS: Record<NonNullable<PatientSearchItem["ehr"]>, string> = {
  elation: "Elation",
  athenahealth: "athenahealth",
  eclinicalworks: "eClinicalWorks",
  redoxfhirsandbox: "Redox FHIR Sandbox",
};

const VALID_TRANSITIONS: Record<PatientStatus, PatientStatus[]> = {
  prospect: ["active", "inactive"],
  active: ["inactive", "deceased"],
  inactive: ["active", "deceased"],
  deceased: [],
};

function isPatientSearchItem(
  patient: Patient | PatientSearchItem | PatientIdentityLike,
): patient is PatientSearchItem {
  return "externalPatientId" in patient;
}

/**
 * Pure business rules for the {@link Patient} entity.
 *
 * The `Patient` type is owned by the server and consumed from the generated
 * api-client; this namespace adds only framework-free behavior over it. No
 * `this`, no mutation — every function takes a plain `Patient` and returns a
 * derived value, so the entity survives object spreads and is trivially testable.
 */
export const PatientEntity = {
  /**
   * Whether the patient may move from its current status to `next`, per the
   * allowed lifecycle transitions.
   */
  canTransitionTo(patient: MaybePatient, next: PatientStatus): boolean {
    if (!patient) return false;
    return VALID_TRANSITIONS[patient.status].includes(next);
  },

  /**
   * Preferred display name: the patient's chosen `actualName` when present,
   * otherwise the legal first/last name.
   */
  displayName(patient: MaybePatientDisplayIdentity): string | null {
    if (!patient) return null;
    const legalName = isPatientSearchItem(patient)
      ? compactStrings([
          patient.firstName,
          patient.middleName,
          patient.lastName,
        ])
      : compactStrings([patient.firstName, patient.lastName]);

    return patient.actualName?.trim() || legalName.join(" ") || null;
  },

  /**
   * Full legal name including middle name when present.
   */
  fullName(patient: MaybePatientDisplayIdentity): string | null {
    if (!patient) return null;
    return (
      compactStrings([
        patient.firstName,
        patient.middleName,
        patient.lastName,
      ]).join(" ") || PatientEntity.displayName(patient)
    );
  },

  parseName(input: string): ReturnType<typeof parseFullName> {
    return parseFullName(input.trim());
  },

  /**
   * Initials used when rendering patient identity affordances.
   */
  initials(patient: MaybePatientDisplayIdentity): string | null {
    if (!patient) return null;
    const first = patient.firstName?.trim().at(0);
    const last = patient.lastName?.trim().at(0);
    const fallback = PatientEntity.displayName(patient)?.trim().at(0);

    return compactStrings([first, last]).join("") || fallback || "?";
  },

  /** Whether the patient is currently active. */
  isActive(patient: MaybePatient): boolean {
    if (!patient) return false;
    return patient.status === "active";
  },

  /**
   * Age in whole years derived from `dob`, or `null` when the date of birth is
   * missing or unparseable.
   */
  age(patient: MaybePatient, now: Date = new Date()): number | null {
    if (!patient) return null;
    const dob = parseNullableDate(patient.dob);
    if (!dob) return null;

    return differenceInYears(now, dob);
  },

  /**
   * Concise demographics label for patient identity rows.
   */
  listMetadata(
    patient: MaybePatientIdentity,
    now: Date = new Date(),
  ): string | null {
    if (!patient) return null;
    if ("patientId" in patient) {
      return compactStrings([
        patient.sex,
        patient.dob ? `DOB ${patient.dob}` : null,
      ]).join(" · ");
    }

    const age = PatientEntity.age(patient, now);
    const sex = patient.sex?.trim().at(0);

    return (
      compactStrings([
        sex,
        age != null ? String(age) : null,
        patient.dob ? `DOB ${patient.dob}` : null,
      ]).join(" · ") || null
    );
  },

  /**
   * Ed patient-detail demographics line: `YYYY/MM/DD • {age}{sexInitial}`.
   * Uses administrative `sex`. Returns null when DOB is missing/unparseable.
   */
  patientLine(patient: MaybePatient, now: Date = new Date()): string | null {
    if (!patient?.dob) return null;
    const age = PatientEntity.age(patient, now);
    if (age == null) return null;

    const dob = patient.dob.replaceAll("-", "/");
    const sex = PatientEntity.sexAbbreviation(patient.sex) ?? "";
    return `${dob} • ${age}${sex}`;
  },

  sexAbbreviation(sex: string | null | undefined): string | null {
    if (!sex) return null;
    return sex.trim().at(0)?.toUpperCase() ?? null;
  },

  address(patient: MaybePatientSearchItem): string | null {
    if (!patient) return null;
    return formatPostalAddress(patient);
  },

  /**
   * The patient's sync provenance, or null when there is none to report: a
   * patient whose system of record is Stitch, or one the server called
   * `synced` without giving us the Stitch id that claim depends on.
   *
   * Every other sync helper derives from this, so they cannot disagree about
   * which of the three states a patient is in.
   */
  syncState(patient: MaybePatientSearchItem): PatientSyncState | null {
    if (!patient) return null;
    if (patient.syncStatus === "synced" && patient.patientId != null) {
      return "synced";
    }
    if (patient.syncStatus === "not_synced") return "not_synced";
    return null;
  },

  isSynced(patient: MaybePatientSearchItem): boolean {
    return PatientEntity.syncState(patient) === "synced";
  },

  syncLabel(patient: MaybePatientSearchItem): string | null {
    switch (PatientEntity.syncState(patient)) {
      case "synced":
        return "Synced";
      case "not_synced":
        return "Not synced";
      default:
        return null;
    }
  },

  syncDescription(patient: MaybePatientSearchItem): string | null {
    const ehrLabel = PatientEntity.ehrLabel(patient) ?? "the EHR";

    switch (PatientEntity.syncState(patient)) {
      case "synced":
        return `This patient has been synced from ${ehrLabel} into ${PRODUCT_NAME.singular}.`;
      case "not_synced":
        return `${PRODUCT_NAME.singular} can see this patient in ${ehrLabel} but has not synced them yet. Select the patient to sync them into ${PRODUCT_NAME.singular}.`;
      default:
        return null;
    }
  },

  ehrConnectorLabel(ehr: NonNullable<PatientSearchItem["ehr"]>): string {
    return EHR_LABELS[ehr];
  },

  ehrLabel(patient: MaybePatientSearchItem): string | null {
    if (!patient) return null;
    return patient.ehr ? PatientEntity.ehrConnectorLabel(patient.ehr) : null;
  },

  accessibleListItemName(patient: MaybePatientSearchItem): string | null {
    if (!patient) return null;
    return compactStrings([
      PatientEntity.displayName(patient),
      PatientEntity.ehrLabel(patient),
      PatientEntity.syncLabel(patient),
      patient?.dob ? `DOB ${patient.dob}` : null,
    ]).join(", ");
  },

  searchResultKey(patient: MaybePatientSearchItem): string | null {
    if (!patient) return null;
    if (patient.patientId != null) return `patient:${patient.patientId}`;
    if (patient.ehr && patient.externalPatientId != null) {
      return `external:${patient.ehr}:${patient.externalPatientId}`;
    }

    return [
      "patient-search",
      PatientEntity.displayName(patient) ?? "",
      patient.dob ?? "",
    ].join(":");
  },

  /**
   * Which sort to request for a given search-box query: best-match-first
   * relevance ranking once the query is long enough for the server's
   * full-text index to use, `lastName` browse order otherwise (including
   * for a blank/empty query, which has nothing to rank against).
   */
  searchSortFor(query: string): {
    sortBy: SearchPatientsSortBy;
    order: "asc" | "desc";
  } {
    if (hasUsableRelevanceQuery(query)) {
      return { sortBy: "relevance", order: "desc" };
    }
    return { sortBy: "lastName", order: "asc" };
  },
} as const;
